import { gql, TypedDocumentNode } from '@apollo/client';
import { OrderItemReturnPolicy, TaxType } from '../../constants';
import { CartPrice, CART_PRICE_FRAGMENT } from '../useCart';

export type OrderCardTransactionDetails = {
  amountPaid: string;
  cardDetail: string;
  displayCard: string;
  lastFour: string;
  source: string;
  __typename: 'RestCardTransactionDetails';
};

export type ShippingStatusDetailed = {
  reason: string;
  request_number: string;
  return_method: string;
  tracking_number: string;
  tracking_url: string;
};

export type ShippingStatus = ShippingStatusDetailed | null | string;

export type ItemStatus =
  | 'pending_authorization'
  | 'on_order'
  | 'shipped'
  | 'paid_in_full'
  | 'payment_pending'
  | 'bill_failure'
  | 'return_requested'
  | 'payment_pending_by_link'
  | 'return_rejected';

export enum OrderItemStatusGroup {
  Open = 'Open',
  Shipped = 'Shipped',
  Returned = 'Returned',
  Refunded = 'Refunded',
  Cancelled = 'Cancelled'
}

export type OrderItemAttributes = {
  __typename: 'RestOrderItemAttributes';
  curatedLookId: number | null;
  curatedLookItemId: number | null;
  amountPaid: string;
  cancelRefundPolicy: OrderItemReturnPolicy;
  category: string;
  color: string;
  customerDeliveryNotes: string | null;
  customization: string | null;
  designerName: string;
  details: {
    __typename: 'RestOrderItemDetails';
    name: string;
    subcategory: string;
  };
  excessShipping: string | null;
  excludedFromRewards: string | null;
  fulfilmentType: number | null;
  images: {
    __typename: 'RestOrderItemImages';
    small: string;
    medium: string;
  };
  isCancellable: boolean;
  isCancellableMessage: string | null;
  cancelRefundPolicyReason: string | null;
  isReturnable: boolean;
  isReturnableMessage: string;
  underPreorderList: boolean;
  markdownPercentage: string | null;
  nonReturnableItemMessage: string | null;
  prices: {
    __typename: 'RestOrderItemPrices';
    depositPrice: CartPrice;
    retailPrice: CartPrice;
    totalPrice: CartPrice;
    totalBalancePayment: CartPrice;
  };
  productHref: string;
  productId: number;
  promotionApplicableMessage: string | null;
  promotionDiscount: string | null;
  quantity: number;
  returnPolicy: OrderItemReturnPolicy;
  refundReason: string | null;
  refundReasonCode: number | null;
  shippingStatus: ShippingStatus;
  shipmentNumber: string;
  shippedOn: string | null;
  tag: string | null;
  taxes: string;
  taxType: TaxType | null;
  trunkshowId: number | null;
  skuId: number;
  status: ItemStatus;
  statusGroup: OrderItemStatusGroup;
  shipmentTrackingUrl: string;
  size: string;
  variantId: number;
};

export type OrderItem = {
  __typename: 'RestOrderItem';
  id: string;
  attributes: OrderItemAttributes;
};

export type OrderTotals = {
  __typename: 'RestOrderTotals';
  shippingAndPackaging: string;
  orderSubtotal: string;
  salesTaxesAndDuties: string;
  orderTotal: {
    __typename: 'RestOrderTotal';
    price: string | number;
    currency: string;
  };
  remainingBalance: {
    __typename: 'RestOrderRemainingBalance';
    price: string | number;
    currency: string;
  };
};

export type OrderItemRelationship = {
  __typename: 'RestOrderItemRelationship';
  id: string;
};

export type OrderDiscounts = {
  __typename: 'RestOrderDiscounts';
  human: string;
  price: string;
  currency: string;
  currencyHtmlCode: string;
};

export type OrderPaymentSummary = {
  __typename: 'RestOrderPaymentSummary';
  giftCode: string | null;
  discounts: OrderDiscounts;
};

export type Order = {
  __typename: 'RestOrder';
  id: string;
  attributes: {
    __typename: 'RestOrderAttributes';
    curatedLookId: number | null;
    cardTransactionDetails: OrderCardTransactionDetails[];
    isGiftOrder: boolean;
    giftNotes: string | null;
    totalAmountPaid: string;
    rewardsToBeEarned: {
      __typename: 'RestOrderRewards';
      totalPoints: number;
    };
    orderNumber: string;
    createdAt: string;
    currencyUnit: string;
    status: string;
    hasCancellableItems: boolean;
    totals: OrderTotals;
    shipmentMethod: number;
    address: {
      __typename: 'RestOrderAddress';
      addressLine1: string;
      addressLine2: string;
      city: string;
      state: string;
      postalCode: string;
      countryCode: string;
      firstName: string;
      lastName: string;
      phone: string;
      source: string;
    };
    firstOrder: boolean;
    paymentSummary: OrderPaymentSummary;
    isAddressChangeable: boolean;
    isPaymentChangeable: boolean;
    hasPendingAuthorizationItems: boolean;
    hasBillFailure: boolean;
    shipmentNumber: string;
    shippedOn: string | number | null;
    stylistId: number | null;
    paymentLink: string | null;
    paymentLinkExpiresOn: string | null;
  };
  relationships: {
    __typename: 'RestOrderRelationships';
    orderItems: {
      __typename: 'RestOrderOrderItems';
      data: OrderItemRelationship[];
    };
  };
};

export type GetOrderQueryVariables = {
  orderNumber: string;
  countryCode: string;
};

export type GetOrderQuery = {
  getOrder: {
    __typename: 'RestOrderPayload';
    data: Order[];
    included: OrderItem[];
  };
};

export const ORDER_ITEM_FRAGMENT = gql`
  fragment OrderItemFragment on RestOrderItem {
    id
    attributes @type(name: "RestOrderItemAttributes") {
      amountPaid: amount_paid
      cancelRefundPolicyReason: cancel_refund_policy_reason
      curatedLookId: shoppable_edit_id
      curatedLookItemId: shoppable_edit_item_id
      cancelRefundPolicy: cancel_refund_policy
      category
      color
      customerDeliveryNotes: customer_delivery_notes
      customization
      designerName: designer_name
      details @type(name: "RestOrderItemDetails") {
        name
        subcategory: product_subcategory
      }
      excessShipping: excess_shipping
      excludedFromRewards: excluded_from_rewards
      fulfilmentType: fulfilment_type
      images: primary_image_urls @type(name: "RestOrderItemImages") {
        small: small_url
        medium: medium_url
      }
      isCancellable: is_cancellable
      isCancellableMessage: is_cancellable_message
      isReturnable: is_returnable
      isReturnableMessage: is_returnable_message
      markdownPercentage: markdown_percentage
      nonReturnableItemMessage: non_returnable_item_message
      prices: formatted_prices @type(name: "RestOrderItemPrices") {
        depositPrice: deposit @type(name: "RestCartPrice") {
          ...CartPriceFragment
        }
        retailPrice: retail_price @type(name: "RestCartPrice") {
          ...CartPriceFragment
        }
        totalPrice: total_price @type(name: "RestCartPrice") {
          ...CartPriceFragment
        }
        totalBalancePayment: total_balance_payment @type(name: "RestCartPrice") {
          ...CartPriceFragment
        }
      }
      productHref: product_href
      productId: product_id
      promotionApplicableMessage: promotion_applicable_message
      promotionDiscount: promotion_discount
      quantity
      returnPolicy: return_policy
      refundReason: refund_reason
      refundReasonCode: refund_reason_code
      shippingStatus: shipping_status
      shipmentNumber: shipping_number
      shippedOn: shipped_on
      skuId: sku_id
      tag
      taxes
      taxType: tax_type
      trunkshowId: trunkshow_id
      status
      statusGroup: status_group
      shipmentTrackingUrl: shipment_tracking_url
      size
      underPreorderList: under_preorder_list
      variantId: variant_id
    }
  }
  ${CART_PRICE_FRAGMENT}
`;

export const ORDER_FRAGMENT = gql`
  fragment OrderFragment on RestOrder {
    id
    attributes @type(name: "RestOrderAttributes") {
      curatedLookId: shoppable_edit_id
      cardTransactionDetails: card_transaction_details @type(name: "RestCardTransactionDetails") {
        amountPaid: amt_paid
        cardDetail: card_detail
        displayCard: display_card
        lastFour: last_four
        source
      }
      isGiftOrder: is_gift_order
      giftNotes: gift_notes
      totalAmountPaid: total_amount_paid
      rewardsToBeEarned: rewards_to_be_earned @type(name: "RestOrderRewards") {
        totalPoints: total_points
      }
      hasCancellableItems: has_cancellable_items
      currencyUnit: currency_unit
      orderNumber: order_number
      createdAt: created_at
      status
      totals @type(name: "RestOrderTotals") {
        shippingAndPackaging: shipping_and_packaging
        orderSubtotal: order_subtotal
        salesTaxesAndDuties: sales_taxes_and_duties
        orderTotal: order_total @type(name: "RestOrderTotal") {
          price
          currency
        }
        remainingBalance: remaining_balance @type(name: "RestOrderRemainingBalance") {
          price
          currency
        }
      }
      shipmentMethod: shipment_method
      address @type(name: "RestOrderAddress") {
        firstName: first_name
        lastName: last_name
        addressLine1: address_line1
        addressLine2: address_line2
        phone
        city
        state
        postalCode: postal_code
        countryCode: country_code
        source
      }
      firstOrder: first_order
      paymentSummary: payment_summary @type(name: "RestOrderPaymentSummary") {
        giftCode: gift_code
        discounts @type(name: "RestOrderDiscounts") {
          human
          price
          currency
          currencyHtmlCode: currency_html_code
        }
      }
      isAddressChangeable: is_address_changeable
      isPaymentChangeable: is_payment_changeable
      hasPendingAuthorizationItems: has_pending_authorization_items
      stylistId: stylist_id
      paymentLink: payment_link
      paymentLinkExpiresOn: payment_link_expires_on
    }
    relationships @type(name: "RestOrderRelationships") {
      orderItems: order_items @type(name: "RestOrderOrderItems") {
        data @type(name: "RestOrderItemRelationship") {
          id
        }
      }
    }
  }
`;

export const GET_ORDER_QUERY: TypedDocumentNode<GetOrderQuery, GetOrderQueryVariables> = gql`
  query GetOrderQuery($orderNumber: string!, $countryCode: String!) {
    getOrder(orderNumber: $orderNumber, countryCode: $countryCode)
      @rest(
        type: "RestOrderPayload"
        endpoint: "moda"
        path: "/v3.5/orders?order_number={args.orderNumber}&include=order_items&country_code={args.countryCode}"
      ) {
      data @type(name: "RestOrder") {
        ...OrderFragment
      }
      included @type(name: "RestOrderItem") {
        ...OrderItemFragment
      }
    }
  }
  ${ORDER_FRAGMENT}
  ${ORDER_ITEM_FRAGMENT}
`;
